import * as dashboardApi from '@/services/dashboardService';
import * as listsApi from '@/services/listsService';
import FileSaver from 'file-saver';
import { fromJS } from 'immutable';
import Papa from 'papaparse';
import type { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { sendAnalytics } from '@/services/analyticsService';
import {
	stateMapping,
	countryMapping
} from '@/components/HeaderRefactored/stateCountryMapping';
import { reportAddRequest } from '../report/reportActions';
import { ReportType } from '../report/reportTypes';
import * as actions from './dashboardActions';
import type {
	Business,
	DashboardAction,
	UserAddedData,
	BusinessContact
} from './dashboardTypes';

const parseLocation = (
	selectedLocation: string | undefined
): { state: string | null; country: string | null } => {
	if (!selectedLocation) {
		return { state: null, country: null };
	}

	const parts = selectedLocation.split(',').map(part => part.trim());

	if (parts.length === 2) {
		const [first, second] = parts;

		// Ensure second part is a recognized country, and first part is a state
		if (
			stateMapping[first] &&
			(countryMapping[second] || second === 'USA')
		) {
			return { state: first, country: second };
		}
	}

	// If there's only one part and it's a valid country, return it
	if (parts.length === 1 && countryMapping[parts[0]]) {
		return { state: null, country: parts[0] };
	}

	// Otherwise, return null values
	return { state: null, country: null };
};

export function* getBusinessesSaga({ payload }: DashboardAction): SagaIterator {
	try {
		let { state, country } = parseLocation(payload.selectedLocation);

		// Fallback to payload if parseLocation fails
		if (!state && !country) {
			state = payload.state;
			country = payload.country;
		}

		let updatedPayload = {
			...payload,
			state: state && stateMapping[state] ? stateMapping[state] : state,
			country:
				country && countryMapping[country]
					? countryMapping[country]
					: country
		};
		if (
			!payload.sort
		) {
			updatedPayload = {
				...updatedPayload,
				sort: { score: -1 },
				sortField: 'score'
			};
		}

		if (updatedPayload.user) {
			delete updatedPayload.user;
		}

		const { data } = yield call(dashboardApi.getBusinesses, updatedPayload);
		yield put(actions.dashboardGetBusinessesSuccess(data));
	} catch (error) {
		yield put(actions.dashboardGetBusinessesFailure(error));
	}
}

export function* getBusinessSaga({ payload }: DashboardAction): SagaIterator {
	try {
		const { data } = yield call(dashboardApi.getBusiness, payload);
		const biz = { ...data.data, ...data.note };

		yield put(actions.dashboardGetBusinessSuccess(biz));
	} catch (error) {
		yield put(actions.dashboardGetBusinessesFailure(error));
	}
}

export function* getAllBusinessesSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		yield put(actions.dashboardGetAllBusinessesLoading());

		const query = yield select(state =>
			state.dashboard.get('query').toJS()
		);

		query.limit = 5000;
		query.listId = payload;
		query.sort = { name: 1 };
		const response = yield call(dashboardApi.getBusinesses, query);

		yield put(
			actions.dashboardGetAllBusinessesSuccess(response.data.data.result)
		);
	} catch (error) {
		throw new Error(error);
	}
}

export function* getSelectBusinessSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		const { data } = yield call(dashboardApi.getBusiness, payload);
		const biz = { ...data.data, ...data.note };

		yield put(actions.dashboardGetSelectBusinessSuccess(biz));
	} catch (error) {
		throw new Error(error);
	}
}

export function* setBusinessReminder({
	payload
}: DashboardAction): SagaIterator {
	try {
		yield put(actions.dashboardSetBusinessReminderLoading());
		const { data } = yield call(dashboardApi.setBusinessReminder, payload);
		const user = yield select(state =>
			state.user.getIn(['authorize', 'user']).toJS()
		);
		yield put(
			actions.dashboardSetBusinessReminderSuccess({
				businessId: payload.businessId,
				businessName: data.businessName,
				note: data.note,
				userEmail: user.email
			})
		);
	} catch (error) {
		yield put(actions.dashboardSetBusinessReminderFailure());
	}
}

export function* deleteBusinessReminder({
	payload
}: DashboardAction): SagaIterator {
	try {
		yield call(dashboardApi.deleteBusinessReminder, payload);
		yield put(
			actions.dashboardDeleteBusinessReminderSuccess({
				businessId: payload.businessId,
				businessName: payload.businessName,
				userEmail: payload.userEmail
			})
		);
	} catch (error) {
		yield put(actions.dashboardSetBusinessReminderFailure());
	}
}

export function* updateBusinessNotesSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		const { businessId, businessName, notes, listId } = payload;

		const dataObj = {
			businessName,
			lead: notes.lead,
			isContacted: notes.isContacted,
			contactType: notes.contactType,
			note: notes.note,
			userAddedData: notes.userAddedData ?? []
		};

		if (listId && listId.length > 0) {
			dataObj.listId = listId;
		}

		const { data } = yield call(
			dashboardApi.updateNoteForBusiness,
			businessId,
			dataObj
		);

		data.currentNoteText = notes.currentNoteText;

		yield put(actions.dashboardUpdateBusinessNotesSuccess(data));
	} catch (error) {
		yield put(actions.dashboardUpdateBusinessNotesFailure(error));
	}
}

export function* getListsSaga(): SagaIterator {
	try {
		const { data } = yield call(listsApi.getLists);

		yield put(actions.dashboardGetListsSuccess(data));
	} catch (error) {
		yield put(actions.dashboardGetListsFailure(error));
	}
}

export function* getSubordinateListsSaga(payload): SagaIterator {
	try {
		const { data } = yield call(
			dashboardApi.getSubordinateLists,
			payload.payload
		);

		yield put(actions.dashboardGetSubordinateListsSuccess(data));
	} catch (error) {
		yield put(actions.dashboardGetSubordinateListsFailure(error));
	}
}

export function* addListSaga({ payload }: DashboardAction): SagaIterator {
	try {
		const query = yield select(state =>
			state.dashboard.get('query').toJS()
		);
		payload.selectedLocation = query.selectedLocation;
		yield put(actions.dashboardAddListLoading());
		const {
			data: { data }
		} = yield call(
			listsApi.addList,
			payload.name,
			payload.businessIds,
			payload.selectedLocation
		);
		yield put(actions.dashboardAddListSuccess(data));
	} catch (error) {
		yield put(actions.dashboardAddListFailure(error));
	}
}

export function* editListSaga({ payload }: DashboardAction): SagaIterator {
	try {
		const {
			data: { data }
		} = yield call(listsApi.editList, payload.listId, payload.name);

		yield put(actions.dashboardEditListSuccess(data));
	} catch (error) {
		yield put(actions.dashboardEditListFailure(error));
	}
}

export function* addBusinessToListSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		const query = yield select(state =>
			state.dashboard.get('query').toJS()
		);
		yield put(actions.dashboardAddListLoading());
		const { data } = yield call(
			listsApi.addBusinessesToList,
			payload.businessIds,
			payload.listId,
			query.selectedLocation
		);

		yield put(actions.dashboardAddBusinessToListSuccess(data));
	} catch (error) {
		yield put(actions.dashboardAddBusinessToListFailure(error));
	}
}

export function* removeBusinessFromListSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		const { data } = yield call(
			listsApi.removeBusinessFromList,
			payload.businessId,
			payload.listId
		);

		const user = yield select(state =>
			state.user.getIn(['authorize', 'user']).toJS()
		);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User removed an item from list',
				properties: {
					businessId: payload.businessId,
					listId: data.data.id,
					listName: data.data.name
				}
			});
		}

		yield put(actions.dashboardRemoveBusinessFromListSuccess(data));
	} catch (error) {
		yield put(actions.dashboardRemoveBusinessFromListFailure(error));
	}
}

export function* removeListSaga({ payload }: DashboardAction): SagaIterator {
	try {
		yield call(listsApi.removeList, payload.id);
		yield put(actions.dashboardRemoveListSuccess(payload));
	} catch (error) {
		yield put(actions.dashboardRemoveListFailure(error));
	}
}

export function* removeListsBatchSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		yield call(listsApi.removeListsBatch, payload.ids);
		yield put(actions.dashboardRemoveListsBatchSuccess(payload.ids));
	} catch (error) {
		yield put(actions.dashboardRemoveListsBatchFailure(error));
	}
}

export function* addBusinessDataSaga({
	payload
}: DashboardAction): SagaIterator {
	yield put(actions.dashboardAddBusinessDataLoading());
	try {
		const { data } = yield call(dashboardApi.postUserGenData, payload);
		yield put(
			actions.dashboardAddBusinessDataSuccess({
				data,
				businessId: payload.businessId,
				userEmail: payload.userEmail
			})
		);
	} catch (error) {
		yield put(actions.dashboardAddBusinessDataFailure(error));
	}
}

export function* deleteBusinessDataSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		const { data } = yield call(dashboardApi.deleteUserGenData, payload);
		sendAnalytics({
			type: 'track',
			userId: payload.userEmail,
			eventName: 'User removed user generated data',
			properties: {
				businessId: data.businessId
			}
		});

		yield put(
			actions.dashboardDeleteBusinessDataSuccess({
				data,
				businessId: payload.businessId
			})
		);
	} catch (error) {
		throw new Error(error);
	}
}

export function* getMapBoxKeySaga(): SagaIterator {
	try {
		const { data } = yield call(dashboardApi.getMapBoxKey);

		yield put(
			actions.dashboardGetMapBoxKeySuccess({
				mapBoxApi: data.data.mapBoxAPIKey
			})
		);
	} catch (error) {
		throw new Error(error);
	}
}

export function* exportBusinessesSaga({
	payload
}: DashboardAction): SagaIterator {
	try {
		const payloadNew = fromJS(payload).toJS();

		const contacted = payloadNew.onlyContactedForExport;

		payloadNew.skip = 0;
		payloadNew.limit = 250;

		if (contacted) {
			payloadNew.contactedYes = true;
			delete payloadNew.contactedNo;
		}

		const {
			data: {
				data: { result: businesses }
			}
		} = yield call(dashboardApi.getBusinesses, payloadNew);

		yield put(
			reportAddRequest({
				type: ReportType.Export,
				businessIds: businesses.map(
					(business: Business) => business._id
				)
			})
		);

		const exportData: any = (businesses as Array<any>).map(
			(business: Business) => {
				let facebookUrl: URL | undefined;

				let userAddedEmails: UserAddedData[];

				if (business.userAddedData) {
					userAddedEmails = business.userAddedData.filter(
						data => data.type === 'email'
					);
				}

				let userAddedWebsites: UserAddedData[];

				if (business.userAddedData) {
					userAddedWebsites = business.userAddedData.filter(
						data => data.type === 'web-link'
					);
				}

				const primaryEmailAddress = () => {
					if (business.facebook?.email)
						return business.facebook.email;
					if (!business.facebook?.email && business.email)
						return business.email;
					if (
						!business.facebook?.email &&
						!business.email &&
						business.web?.email
					)
						return business.web.email;
					if (
						!business.facebook?.email &&
						!business.email &&
						!business.web?.email &&
						userAddedEmails?.length
					)
						return userAddedEmails[0].data;

					return '';
				};

				const secondaryEmailAddress = () => {
					if (business.facebook?.email && business.email)
						return business.email;

					if (
						business.facebook?.email &&
						!business.email &&
						business.web?.email
					)
						return business.web.email;

					if (
						business.facebook?.email &&
						!business.email &&
						!business.web?.email &&
						userAddedEmails?.length
					)
						return userAddedEmails[0].data;

					if (
						business.facebook?.email &&
						!business.email &&
						!business.web?.email &&
						!userAddedEmails?.length
					)
						return '';

					if (
						!business.facebook?.email &&
						business.email &&
						business.web?.email
					)
						return business.web?.email;

					if (
						!business.facebook?.email &&
						business.email &&
						!business.web?.email &&
						userAddedEmails?.length
					)
						return userAddedEmails[0].data;

					if (
						!business.facebook?.email &&
						business.email &&
						!business.web?.email &&
						!userAddedEmails?.length
					)
						return '';

					if (
						!business.facebook?.email &&
						!business.email &&
						business.web?.email &&
						userAddedEmails?.length
					)
						return userAddedEmails[0].data;

					if (
						!business.facebook?.email &&
						!business.email &&
						business.web?.email &&
						!userAddedEmails?.length
					)
						return '';

					if (
						!business.facebook?.email &&
						!business.email &&
						!business.web?.email &&
						userAddedEmails?.length &&
						userAddedEmails?.length >= 2
					)
						return userAddedEmails[1].data;

					if (
						!business.facebook?.email &&
						!business.email &&
						!business.web?.email &&
						userAddedEmails?.length
					)
						return '';

					return '';
				};

				const staffInformation = (): string => {
					const normalizeName = (name: string): string => {
						const normalize = (name: string): string => {
							return name
								.toLowerCase() // Convert the name to lowercase
								.split(' ') // Split the name into words
								.filter(word => word.length > 0) // Remove empty words
								.map(word =>
									(
										word.charAt(0).toUpperCase() +
										word.slice(1)
									).trim()
								) // Capitalize the first letter of each word
								.join(' ');
						};

						// grabs everything after comma (,)
						const getTitle = (name: string): string =>
							name.split(',')[1]?.trim() || '';

						const getNormalizedTitle = (name: string): string => {
							return normalize(getTitle(name));
						};

						const title = getNormalizedTitle(name);
						const nameWithoutTitle = name
							.replace(/,\s*(.*)|\(([^)]+)\)/, '')
							.trim();
						const normalizedPersonName =
							normalize(nameWithoutTitle);

						return title
							? `${normalizedPersonName} (${title.trim()})`
							: normalizedPersonName;
					};

					const removeExactDuplicates = (arr: string[]): string[] => {
						return [...new Set(arr)];
					};

					const getManagers = (): string[] => {
						if (
							business.businessBureau?.businessManagersInfo &&
							business.businessBureau.businessManagersInfo
								.length > 0
						) {
							return business.businessBureau.businessManagersInfo;
						}

						return [''];
					};

					const getContacts = (): string[] => {
						if (
							business?.contactNames &&
							business?.contactNames?.length > 0
						) {
							return business.contactNames.map(
								(contact: BusinessContact) => {
									const firstTitle = contact?.titles.find(
										title =>
											title.toLowerCase() !==
											'contact name'
									);
									return `${contact.name} ${firstTitle ? `,${firstTitle}` : ''}`;
								}
							);
						}
						return [''];
					};

					const normalizedNames: string[] = [
						...getManagers(),
						...getContacts()
					].map(normalizeName);
					return removeExactDuplicates(normalizedNames).join('\n');
				};

				const businessWebsite = () => {
					if (business.website) return business.website;
					if (!business.website && business.facebook?.website)
						return business.facebook.website;

					if (
						!business.website &&
						!business.facebook?.website &&
						userAddedWebsites?.length
					)
						return userAddedWebsites[0].linkData;

					return '';
				};

				if (
					business?.facebook?.url &&
					business?.facebook?.url.includes(
						'https://www.facebook.com/'
					)
				) {
					facebookUrl = new window.URL(business?.facebook?.url);
				}
				if (contacted) {
					return {
						Name: business.name
							.replace('ː', ':')
							.replace('’', "'")
							.replace(' ', ' ')
							.replace('˂', '<')
							.replace('˃', '>'),
						'RE2 Score': business.score,
						Address: business.address.address,
						Contacted: business.isContacted ? 'YES' : 'NO',
						Lead: business?.lead ? 'YES' : 'NO',
						Categories: business.categories.display,
						'Phone Number': business.phone.display,
						'Primary Email Address': primaryEmailAddress(),
						'Secondary Email Address': secondaryEmailAddress(),
						Website: businessWebsite(),
						'Yelp Star Rating': business.yelp.rating,
						'Yelp Review Count': business.yelp.reviews,
						'User Notes': business.note,
						'Type of Entity':
							business?.businessBureau?.typeOfEntity,
						'Staff Information': staffInformation(),
						'Better Business Bureau Rating':
							business?.businessBureau?.rating,
						Facebook: facebookUrl
							? `${facebookUrl?.origin}/${facebookUrl?.pathname
									.split('/')
									.filter(
										(value, index) =>
											value.length > 0 &&
											index - 1 !==
												facebookUrl?.pathname.split('/')
													.length &&
											value !== 'about'
									)
									.join('/')}`
							: '',

						'FB Messenger': business?.facebook?.messengerUrl
							? `https://${business?.facebook?.messengerUrl}`
							: '',

						'Facebook Look-up': `https://www.facebook.com/search/top?q=${encodeURI(
							`${business.name} ${business?.address?.city}, ${business?.address?.state}`
						)}`,

						'Instagram Search': `https://www.google.com/search?q=${business.address?.city}+${business.name}+instagram`
					};
				}
				return {
					Name: business.name
						.replace('ː', ':')
						.replace('’', "'")
						.replace(' ', ' ')
						.replace('˂', '<')
						.replace('˃', '>'),
					'RE2 Score': business.score,
					Address: business.address.address,
					Contacted: business.isContacted ? 'YES' : 'NO',
					Lead: business?.lead ? 'YES' : 'NO',
					Categories: business.categories.display,
					'Phone Number': business.phone.display,
					Website: businessWebsite(),
					'Yelp Star Rating': business.yelp.rating,
					'Yelp Review Count': business.yelp.reviews,
					'User Notes': business.note,
					'Type of Entity': business?.businessBureau?.typeOfEntity,
					'Staff Information': staffInformation(),
					'Better Business Bureau Rating':
						business?.businessBureau?.rating,
					Facebook: facebookUrl
						? `${facebookUrl?.origin}/${facebookUrl?.pathname
								.split('/')
								.filter(
									(value, index) =>
										value.length > 0 &&
										index - 1 !==
											facebookUrl?.pathname.split('/')
												.length &&
										value !== 'about'
								)
								.join('/')}`
						: ''
				};
			}
		);

		const list = yield select(state =>
			state.dashboard.getIn(['lists', 'selected']).toJS()
		);

		const currentDate = new Date(Date.now()).toISOString().split('T')[0];

		FileSaver.saveAs(
			new Blob([Papa.unparse(exportData)], {
				type: 'text/csv;charset=utf-8'
			}),
			`RE2 export${Object.keys(list).length > 0 ? `${list.name}` : ''}${
				contacted ? '-contacted-' : '-all-businesses-'
			}${currentDate}.csv`
		);

		yield put(actions.dashboardExportBusinessesSuccess());
	} catch (error) {
		yield put(actions.dashboardExportBusinessesFailure(error));
	}
}

export function* getCommunicationCenterBusinesses({
	payload
}: DashboardAction): SagaIterator {
	try {
		const { data } = yield call(
			dashboardApi.getCommunicationCenterBusinesses,
			payload
		);
		yield put(
			actions.dashboardGetCommunicationCenterBusinessesSuccess(data)
		);
	} catch (error) {
		throw new Error(error);
	}
}

export function* getGooglePlaces({ payload }: DashboardAction): SagaIterator {
	try {
		const response = yield call(dashboardApi.getGooglePlaces, payload);

		if (response.status !== 200) {
			yield put(actions.dashboardGetGooglePlacesFailure());
			return;
		}

		yield put(actions.dashboardGetGooglePlacesSuccess(response.data.data));
	} catch (error) {
		throw new Error(error);
	}
}

export function* requestNewLocation({
	payload
}: DashboardAction): SagaIterator {
	try {
		const response = yield call(dashboardApi.requestNewLocation, payload);

		if (response.status !== 200) {
			yield put(actions.dashboardRequestNewLocationFailure());
			return;
		}

		yield put(actions.dashboardRequestNewLocationSuccess());
	} catch (error) {
		throw new Error(error);
	}
}

export function* requestAddLocationFeature(): SagaIterator {
	try {
		const response = yield call(dashboardApi.requestAddLocationFeature);
		if (response.status !== 200) {
			yield put(actions.dashboardRequestAddLocationFeatureFailure());
			return;
		}

		yield put(actions.dashboardRequestAddLocationFeatureSuccess());
	} catch (error) {
		yield put(actions.dashboardRequestAddLocationFeatureFailure());
		throw new Error(error);
	}
}

export function* getActivities({ payload }: DashboardAction): SagaIterator {
	try {
		yield put(actions.dashboardGetActivitiesLoading());
		const {
			data: { data }
		} = yield call(dashboardApi.getActivities, payload);

		yield put(actions.dashboardGetActivitiesSuccess(data));
	} catch (error) {
		yield put(actions.dashboardGetActivitiesFailure(error));
	}
}

export function* createActivityNote({
	payload
}: DashboardAction): SagaIterator {
	try {
		yield put(actions.dashboardCreateActivityNoteLoading());
		const response = yield call(dashboardApi.createActivityNote, payload);

		if (response.status !== 200) {
			yield put(
				actions.dashboardCreateActivityNoteFailure({
					message: response.data.message
				})
			);
			return;
		}

		yield put(actions.dashboardCreateActivityNoteSuccess());
	} catch (error) {
		yield put(actions.dashboardCreateActivityNoteFailure(error));
	}
}

export function* getActivityEmailOutbound({
	payload
}: DashboardAction): SagaIterator {
	try {
		yield put(actions.dashboardGetActivityEmailOutboundLoading(payload));
		const {
			data: { email }
		} = yield call(dashboardApi.getActivityEmailOutbound, payload);

		yield put(actions.dashboardGetActivityEmailOutboundSuccess(email));
	} catch (error) {
		yield put(
			actions.dashboardGetActivityEmailOutboundFailure({
				message: error,
				...payload
			})
		);
	}
}

export function* getActivityEmailInbound({
	payload
}: DashboardAction): SagaIterator {
	try {
		yield put(actions.dashboardGetActivityEmailInboundLoading(payload));
		const {
			data: { email }
		} = yield call(dashboardApi.getActivityEmailInbound, payload);

		yield put(actions.dashboardGetActivityEmailInboundSuccess(email));
	} catch (error) {
		yield put(
			actions.dashboardGetActivityEmailInboundFailure({
				message: error,
				...payload
			})
		);
	}
}
